import ApplicationController from "../application_component_controller";
import { fetchEmpty, withRedirectTo } from "hb/lib/fetch";
import URL from "hb/lib/url";
import * as Turbo from "@hotwired/turbo";

import { useEditing } from "hb/insights/use_editing";
import { useInlineEditing } from "hb/insights/use_inline_editing";
import { useOutlets } from "hb/insights/use_outlets";
import { useListen } from "hb/insights/use_listen";
import { deepEqual } from "hb/lib/utils";

export default class extends ApplicationController {
  static targets = ["title", "refreshIcon", "autoRefreshLabel", "progressRing"];
  static values = {
    dashboardPath: String,
    widgetsPreviewPath: String,
    titleEditable: Boolean,
  };

  connect() {
    useEditing(this);
    if (this.titleEditableValue) {
      useInlineEditing(this, { targets: ["title"] });
    }
    useOutlets(this, { components: ["grid"] });
    useListen(this);

    this.originalWidgetConfigs = new Map();
    this.newlyAddedWidgets = new Set();
    this.autoRefreshInterval = null;
    this.autoRefreshSeconds = null;
    this.progressAnimationFrame = null;
    this.progressStartTime = null;

    this.listenWindow("queryVisConfig:update", this.onQueryVisConfigUpdate);
    this.listenWindow("dashboard:addWidget", this.onAddWidget);
    this.listenWindow("dashboard:refreshWidget", this.onRefreshWidget);
    this.listenWindow("dashboard:editWidget", this.onEditWidget);
  }

  disconnect() {
    this.clearAutoRefresh();
  }

  outletsConnected() {
    if (URL.getParam("edit")) {
      URL.replaceParam("edit", "");
      this.edit();
    }
  }

  edit() {
    this.outletComponent("grid").edit();

    this.originalWidgetConfigs.clear();
    this.outletComponent("grid")
      .outletComponents("grid-item")
      .forEach((gridItem) => {
        gridItem.edit();
        this.originalWidgetConfigs.set(gridItem.id, {
          config: gridItem.widgetValue.config,
          grid: gridItem.gridPosition,
        });
      });
  }

  cancel() {
    this.emitWindow("dashboard:closePanels");

    this.outletComponent("grid").cancel();

    this.outletComponent("grid")
      .outletComponents("grid-item")
      .forEach((gridItem) => {
        if (this.newlyAddedWidgets.has(gridItem.id)) {
          gridItem.deleteWidget();
          gridItem.element.remove();
        } else {
          const original = this.originalWidgetConfigs.get(gridItem.id);

          gridItem.cancel();

          if (!deepEqual(gridItem.localWidget.config, original.config)) {
            if (gridItem.widgetTypeValue === "query_vis") {
              gridItem.updateQueryVisConfig(original.config);
            } else {
              gridItem.resetConfig(original.config);
              gridItem.refreshProductWidget();
            }
          }

          this.outletComponent("grid").grid.update(
            gridItem.element,
            original.grid,
          );
        }
      });

    this.newlyAddedWidgets.clear();
  }

  add() {
    this.outletComponent("grid").add();
  }

  refresh() {
    this.emitWindow("dashboard:refresh");

    this.refreshIconTarget.classList.remove("rotating");
    void this.refreshIconTarget.offsetWidth;
    this.refreshIconTarget.classList.add("rotating");
  }

  async clone({ params: { path } }) {
    await withRedirectTo({ keepParams: ["ts"] })(
      fetchEmpty({ method: "POST", path, snakeCaseBody: false }),
    );
  }

  async saveTs() {
    await fetchEmpty({
      jsonBody: { ts: URL.getParam("ts") },
      method: "PATCH",
      path: `${this.dashboardPathValue}/save_timestamp`,
      snakeCaseBody: false,
    });

    window.UnobtrusiveFlash.showFlash([["notice", "Default time range saved"]]);
  }

  async save() {
    this.emitWindow("dashboard:closePanels");

    const widgets = this.outletComponent("grid").save();
    await this.saveData(this.titleTarget.textContent, widgets);
    this.newlyAddedWidgets.clear();
  }

  async saveData(title, widgets) {
    const updatePath = this.hasDashboardPathValue
      ? this.dashboardPathValue
      : window.location.pathname;
    const dashboard = { widgets };
    if (this.titleEditableValue) {
      dashboard.title = title;
    }

    const res = await withRedirectTo({ push: true, keepParams: true })(
      fetchEmpty({
        jsonBody: { dashboard },
        method: "PUT",
        path: updatePath,
        snakeCaseBody: false,
      }),
    );

    const errorMessage = res?.headers?.get("X-Flash-Error");
    if (errorMessage && window.UnobtrusiveFlash) {
      window.UnobtrusiveFlash.showFlash([["error", errorMessage]]);
    }
  }

  onQueryVisConfigUpdate({ querySessionId, config }) {
    const gridQuerySessionId = querySessionId.replace("cp-", "");
    const gridItem = this.outletComponent("grid")
      .outletComponents("grid-item")
      .find((item) => item.querySessionIdValue === gridQuerySessionId);
    const gridLocalConfig = gridItem.localWidget.config;

    if (gridItem && !deepEqual(gridLocalConfig, config)) {
      gridItem.updateQueryVisConfig(config);
    }
  }

  onEditWidget() {
    if (!this.editing) {
      this.edit();
    }
  }

  async onRefreshWidget(widgetData) {
    try {
      const payload = {
        widget: widgetData,
        ...URL.paramsToObject(URL.slice(["ts", "timezone"])),
      };

      if (this.editing) {
        const originalWidget = this.originalWidgetConfigs.get(widgetData.id);
        if (originalWidget) {
          payload.original_widget = originalWidget;
        }
      }

      const response = await fetch(this.widgetsPreviewPathValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "text/vnd.turbo-stream.html",
          "X-CSRF-Token": document.querySelector('[name="csrf-token"]').content,
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        const html = await response.text();
        Turbo.renderStreamMessage(html);
      } else {
        console.error("Widget refresh failed:", response.statusText);
      }
    } catch (error) {
      console.error("Widget refresh error:", error);
    }
  }

  async onAddWidget(widgetData) {
    const widgetId = crypto.randomUUID();
    this.newlyAddedWidgets.add(widgetId);
    if (!this.editing) {
      this.edit();
    }

    const grid = this.outletComponent("grid").grid;
    const placeholder = document.createElement("div");
    placeholder.id = `widget_${widgetId}`;
    placeholder.className = "grid-stack-item";
    placeholder.setAttribute("gs-id", widgetId);
    placeholder.innerHTML = `
      <div class="grid-stack-item-content">
        <div class="text-center p-4">
          <i class="fa fa-spinner fa-spin fa-2x"></i>
          <p class="mt-2">Loading widget...</p>
        </div>
      </div>
    `;

    grid.addWidget(placeholder, { x: 0, y: 0, w: 12, h: 3 });

    const widgetConfig = {
      id: widgetId,
      grid: {
        x: 0,
        y: 0,
        w: 12,
        h: 3,
      },
      editing: true,
      ...widgetData,
    };

    const response = await fetch(this.widgetsPreviewPathValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "text/vnd.turbo-stream.html",
        "X-CSRF-Token": document.querySelector('[name="csrf-token"]').content,
      },
      body: JSON.stringify({
        widget: widgetConfig,
        ...URL.paramsToObject(URL.slice(["ts", "timezone"])),
      }),
    });

    if (response.ok) {
      const turboStream = await response.text();
      Turbo.renderStreamMessage(turboStream);
    }
  }

  setAutoRefresh({ params: { interval } }) {
    this.clearAutoRefresh();

    if (interval === "off") {
      this.autoRefreshLabelTarget.textContent = "";
      this.progressRingTarget
        .closest(".refresh-btn-with-progress")
        .classList.remove("auto-refresh-active");
      return;
    }

    const seconds = parseInt(interval, 10);
    this.autoRefreshSeconds = seconds;

    // Update label
    if (seconds < 60) {
      this.autoRefreshLabelTarget.textContent = `${seconds}s `;
    } else if (seconds === 60) {
      this.autoRefreshLabelTarget.textContent = "1m ";
    } else if (seconds < 3600) {
      const minutes = Math.floor(seconds / 60);
      this.autoRefreshLabelTarget.textContent = `${minutes}m `;
    } else {
      const hours = Math.floor(seconds / 3600);
      this.autoRefreshLabelTarget.textContent = `${hours}h `;
    }

    // Show progress ring and start animation
    this.progressRingTarget
      .closest(".refresh-btn-with-progress")
      .classList.add("auto-refresh-active");
    this.startProgressAnimation(seconds);

    // Set up interval
    this.autoRefreshInterval = setInterval(() => {
      this.refresh();
      this.startProgressAnimation(seconds);
    }, seconds * 1000);
  }

  startProgressAnimation(seconds) {
    const circle = this.progressRingTarget.querySelector(
      ".progress-ring-circle",
    );
    const circumference = 75.4; // 2 * PI * radius (radius = 12)

    this.progressStartTime = Date.now();
    const duration = seconds * 1000;

    const animate = () => {
      const elapsed = Date.now() - this.progressStartTime;
      const progress = Math.min(elapsed / duration, 1);
      const offset = circumference * progress;

      circle.style.strokeDashoffset = offset;

      if (progress < 1) {
        this.progressAnimationFrame = requestAnimationFrame(animate);
      }
    };

    // Reset and start
    circle.style.strokeDashoffset = 0;
    this.progressAnimationFrame = requestAnimationFrame(animate);
  }

  clearAutoRefresh() {
    if (this.autoRefreshInterval) {
      clearInterval(this.autoRefreshInterval);
      this.autoRefreshInterval = null;
      this.autoRefreshSeconds = null;
    }

    if (this.progressAnimationFrame) {
      cancelAnimationFrame(this.progressAnimationFrame);
      this.progressAnimationFrame = null;
      this.progressStartTime = null;
    }
  }
}
